<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.absenteeism'),
                        href: meta.links.absenteeism_index,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div class="flex gap-x-1 items-center">
                        <BadgePill>
                            <a :href="meta.links.stats_index">{{ $t('messages.graphs') }}</a>
                        </BadgePill>
                        <BadgePill>
                            <InertiaLink :href="meta.links.month_index">{{ $t('messages.month') }}</InertiaLink>
                        </BadgePill>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div>
            <NavLinks :links="meta.navStagesLinks" />
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-2">
                <h5 class="card-title">
                    {{ meta.info.summary }}
                </h5>
                <div>
                    <apexchart
                        type="bar"
                        height="350"
                        :series="absenteeismChartValuesData.series"
                        :options="absenteeismChartValuesData"
                    />
                </div>
            </div>
            <div class="md:col-span-1">
                <apexchart type="pie" height="250" :series="pieChartData.series" :options="pieChartData" />
            </div>
            <div class="md:col-span-3">
                <apexchart
                    type="bar"
                    height="350"
                    :series="absenteeismChartValuesSansGenderChartData.series"
                    :options="absenteeismChartValuesSansGenderChartData"
                />
            </div>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                            null-option
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-if="options.school_years.length">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-if="options.other_school_years.length">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.compare_with_school_year')" for="second_school_year_id" />
                        <FormSelect
                            id="second_school_year_id"
                            v-model="form.second_school_year_id"
                            :options="options.other_school_years"
                            class="mt-1 w-full"
                            null-option
                        />
                        <FormError :value="form.errors.second_school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <FormLabel :value="$t('messages.absenteeism')" for="absenteeismPercent"> (>%)</FormLabel>
                    <FormInput
                        id="absenteeismPercent"
                        v-model="form.absenteeismPercent"
                        name="absenteeismPercent"
                        type="number"
                        min="1"
                        max="99"
                        step="1"
                        class="mt-1 w-full"
                    />
                    <FormError :value="form.errors.absenteeismPercent" class="mt-2" />
                </div>

                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-2 flex gap-x-2 items-center">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                        <AppButton element="a" :href="meta.links.attendance_stats_index">
                            {{ $t('messages.stats') }}
                        </AppButton>
                    </div>
                </div>
            </div>
        </form>

        <div v-if="attendanceRecords.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div class="flex flex-col mt-5" v-else>
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.groups', 2) }}
                                </th>
                                <template v-if="filters.second_school_year_id">
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ previousSchoolYear.year_range }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ currentSchoolYear.year_range }}
                                    </th>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.trends', 1) }}
                                    </th>
                                </template>
                                <template v-else>
                                    <th
                                        class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ currentSchoolYear.year_range }}
                                    </th>
                                </template>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(attendanceRecord, index) in records"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                :key="attendanceRecord.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span v-if="attendanceRecord.student.is_study_variable">*</span>
                                    <InertiaLink
                                        :href="attendanceRecord.student.links.show"
                                        class="text-indigo-500"
                                        v-if="attendanceRecord.student.active"
                                    >
                                        {{ attendanceRecord.student.full_name }}
                                    </InertiaLink>
                                    <template v-else>
                                        <span> {{ attendanceRecord.student.full_name }}</span>
                                        <BadgePill color="yellow" v-if="!attendanceRecord.student.active">
                                            {{ $t('messages.inactive') }}
                                        </BadgePill>
                                    </template>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ attendanceRecord.student.group.name }}
                                </td>

                                <template v-if="filters.second_school_year_id">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ attendanceRecord.previousYearPercentageString }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        {{ attendanceRecord.percentageString }}
                                    </td>
                                    <td>
                                        <div class="flex justify-start">
                                            <span v-if="attendanceRecord.trend === 'up'">
                                                <svg width="24" height="24" style="color: #fc8181">
                                                    <rect width="24" height="24" fill="none" rx="0" ry="0" />
                                                    <path
                                                        fill-rule="evenodd"
                                                        clip-rule="evenodd"
                                                        d="M22.5282 5.24023H13.6982C13.4482 5.24023 13.2282 5.39023 13.1382 5.62023C13.0482 5.85023 13.1082 6.11023 13.2882 6.28023L16.2282 9.04023L13.0782 12.1902L9.26817 8.38023C9.15817 8.26023 9.00817 8.20023 8.84817 8.20023C8.68817 8.20023 8.53817 8.26023 8.42817 8.38023L1.30817 15.4902C1.18817 15.6002 1.12817 15.7502 1.12817 15.9102C1.12817 16.0702 1.18817 16.2202 1.30817 16.3302L3.55817 18.5802C3.67817 18.7002 3.82817 18.7602 3.97817 18.7602C4.12817 18.7602 4.28817 18.7002 4.39817 18.5802L8.82817 14.1502L12.6382 17.9702C12.8682 18.2002 13.2482 18.2002 13.4882 17.9702L19.3182 12.1502L22.0882 15.0802C22.2582 15.2602 22.5182 15.3202 22.7482 15.2302C22.9782 15.1402 23.1282 14.9202 23.1282 14.6702V5.84023C23.1282 5.51023 22.8582 5.24023 22.5282 5.24023Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </span>
                                            <span v-else-if="attendanceRecord.trend === 'down'">
                                                <svg width="24" height="24" style="color: #68d391">
                                                    <rect width="24" height="24" fill="none" rx="0" ry="0" />
                                                    <path
                                                        fill-rule="evenodd"
                                                        clip-rule="evenodd"
                                                        d="M22.5282 18.7598H13.6982C13.4482 18.7598 13.2282 18.6098 13.1382 18.3798C13.0482 18.1498 13.1082 17.8898 13.2882 17.7198L16.2282 14.9598L13.0782 11.8098L9.26817 15.6198C9.15817 15.7398 9.00817 15.7998 8.84817 15.7998C8.68817 15.7998 8.53817 15.7398 8.42817 15.6198L1.30817 8.50977C1.18817 8.39977 1.12817 8.24977 1.12817 8.08977C1.12817 7.92977 1.18817 7.77977 1.30817 7.66977L3.55817 5.41977C3.67817 5.29977 3.82817 5.23977 3.97817 5.23977C4.12817 5.23977 4.28817 5.29977 4.39817 5.41977L8.82817 9.84977L12.6382 6.02977C12.8682 5.79977 13.2482 5.79977 13.4882 6.02977L19.3182 11.8498L22.0882 8.91977C22.2582 8.73977 22.5182 8.67977 22.7482 8.76977C22.9782 8.85977 23.1282 9.07977 23.1282 9.32977V18.1598C23.1282 18.4898 22.8582 18.7598 22.5282 18.7598Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </span>
                                            <span class="ml-3">
                                                {{ attendanceRecord.diff }}
                                            </span>
                                        </div>
                                    </td>
                                </template>

                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500" v-else>
                                    {{ attendanceRecord.percentageString }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import NavLinks from '@/components/NavLinks.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        FormInput,
        NavLinks,
        BadgePill,
        PanelPage,
        AppButton,
        FormSelect,
        FormLabel,
        FormError,
        EmptyState,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'filters', 'options', 'attendanceRecords', 'charts', 'currentSchoolYear', 'previousSchoolYear'],
    data() {
        return {
            absenteeismChartValuesData: JSON.parse(JSON.stringify(this.charts.absenteeismChartValuesData)),
            absenteeismChartValuesSansGenderChartData: JSON.parse(
                JSON.stringify(this.charts.absenteeismChartValuesSansGenderChartData),
            ),
            pieChartData: JSON.parse(JSON.stringify(this.charts.pieChartData)),
            records: JSON.parse(JSON.stringify(this.attendanceRecords)),
            form: this.$inertia.form({
                stage_id: this.filters.stage_id,
                school_year_id: this.filters.school_year_id,
                group_id: this.filters.group_id,
                second_school_year_id: this.filters.second_school_year_id,
                absenteeismPercent: this.filters.absenteeismPercent,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index, {
                preserveState: false,
                preserveScroll: false,
            });
        },
    },
};
</script>
